import { useCallback, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";

import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";
import { ColumnOption } from "types/table";

interface UseClientTableColumnLayoutParams<TData> {
  tableConfigKey: string;
  allColumns: ColumnDef<TData>[];
  primaryColumnIds?: string[];
}

export const useClientTableColumnLayout = <TData>({
  tableConfigKey,
  allColumns,
  primaryColumnIds = [],
}: UseClientTableColumnLayoutParams<TData>) => {
  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const handleToggleColumn = useCallback(
    (columnId: string) => {
      toggleColumnVisibility(tableConfigKey, columnId);
    },
    [tableConfigKey, toggleColumnVisibility],
  );

  const hiddenColumnIds = useMemo(
    () => new Set(tableColumnsConfig[tableConfigKey]?.hidden ?? []),
    [tableColumnsConfig, tableConfigKey],
  );

  const columnOptions = useMemo<ColumnOption[]>(
    () =>
      allColumns
        .filter(
          (col): col is ColumnDef<TData> & { id: string; header: string } =>
            typeof col.header === "string" &&
            col.id !== undefined &&
            !primaryColumnIds.includes(col.id) &&
            col.id !== "action",
        )
        .map((col) => ({ id: col.id, label: col.header })),
    [allColumns, primaryColumnIds],
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => !hiddenColumnIds.has(col.id ?? "")),
    [allColumns, hiddenColumnIds],
  );

  return {
    tableColumnsConfig,
    columnOptions,
    visibleColumns,
    handleToggleColumn,
  };
};
